import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  FileText,
  Download,
  CheckCircle2,
  Loader2,
  PenLine,
  ShieldCheck,
  AlertTriangle,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import api, { API } from "../lib/api";
import { toast } from "sonner";

export default function RecruiterSignAgreement() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [signedName, setSignedName] = useState("");
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/recruiters/agreement/${token}`);
        setData(res.data);
      } catch (e) {
        setError(
          e?.response?.data?.detail ||
            "This agreement link is invalid or has expired."
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const submit = async () => {
    if (!signedName.trim()) return toast.error("Please type your full name");
    if (!agreedTerms) return toast.error("Please confirm you accept the terms");
    setSubmitting(true);
    try {
      await api.post(`/recruiters/agreement/${token}`, {
        signed_name: signedName.trim(),
        agreed_terms: true,
      });
      toast.success("Agreement signed successfully");
      const res = await api.get(`/recruiters/agreement/${token}`);
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit signature");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading)
    return (
      <div className="min-h-screen bg-cct-ambient flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-cct-blue" />
      </div>
    );

  if (error)
    return (
      <div className="min-h-screen bg-cct-ambient">
        <Header minimal />
        <main className="max-w-xl mx-auto px-4 py-16 text-center">
          <div className="inline-flex w-16 h-16 rounded-full bg-red-50 items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="mt-4 font-display text-3xl font-semibold text-slate-900">
            Agreement Unavailable
          </h1>
          <p className="mt-2 text-slate-600">{error}</p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-1 text-sm text-cct-blue hover:underline"
          >
            <ArrowLeft className="w-4 h-4" /> Back to portal
          </Link>
        </main>
      </div>
    );

  const already = !!data?.agreement?.signed_at;

  return (
    <div className="min-h-screen bg-cct-ambient">
      <Header minimal />
      <main className="max-w-3xl mx-auto px-4 pb-20">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-cct-blue mb-5"
        >
          <ArrowLeft className="w-4 h-4" /> Back to portal
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#1E3A8A] to-[#2A4FAE] text-white p-6 sm:p-8"
        >
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#C49A3A]/30 blur-3xl" />
          <div className="relative">
            <div className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase font-semibold text-amber-300">
              <PenLine className="w-3.5 h-3.5" /> Recruiter Agreement
            </div>
            <h1 className="mt-2 font-display text-3xl sm:text-4xl font-semibold">
              {data.agreement.subject}
            </h1>
            <p className="mt-2 text-white/75 text-sm">
              Prepared for{" "}
              <span className="text-amber-300 font-semibold">
                {data.recruiter.full_name}
              </span>{" "}
              · {data.recruiter.company_name}
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mt-5 bg-white border border-slate-200 rounded-2xl shadow-sm p-6 sm:p-8"
        >
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
            Agreement Terms
          </div>
          <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
            {data.agreement.body}
          </div>

          {data.agreement.has_file && (
            <a
              href={`${API}/recruiters/agreement/${token}/file`}
              target="_blank"
              rel="noreferrer"
              className="mt-5 inline-flex items-center gap-2 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg px-3.5 py-2.5 text-sm text-slate-800"
            >
              <FileText className="w-4 h-4 text-cct-blue" />
              <span className="flex-1 truncate">
                {data.agreement.filename || "Agreement document"}
              </span>
              <Download className="w-4 h-4 text-slate-500" />
            </a>
          )}
        </motion.div>

        {already ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mt-5 bg-white border border-emerald-200 rounded-2xl shadow-sm p-6 text-center"
          >
            <div className="inline-flex w-14 h-14 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 items-center justify-center shadow-md">
              <CheckCircle2 className="w-7 h-7 text-white" />
            </div>
            <h2 className="mt-4 font-display text-2xl font-semibold text-slate-900">
              Agreement Signed
            </h2>
            <p className="mt-1 text-slate-600 text-sm">
              Signed by{" "}
              <span className="font-semibold">
                {data.agreement.signed_name}
              </span>{" "}
              on{" "}
              {new Date(data.agreement.signed_at).toLocaleString()}
            </p>
            <p className="mt-3 text-xs text-slate-500">
              The admissions team will activate your account shortly and email
              you a welcome link to set your password.
            </p>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-5 bg-white border border-slate-200 rounded-2xl shadow-sm p-6 sm:p-8"
          >
            <div className="flex items-center gap-2 mb-4">
              <PenLine className="w-5 h-5 text-cct-blue" />
              <h3 className="font-display text-xl font-semibold text-slate-900">
                Sign your agreement
              </h3>
            </div>

            <label className="block">
              <span className="text-sm font-medium text-slate-700">
                Type your full legal name as your electronic signature{" "}
                <span className="text-[#C49A3A]">*</span>
              </span>
              <input
                value={signedName}
                onChange={(e) => setSignedName(e.target.value)}
                placeholder="e.g. Jane Doe"
                className="mt-1.5 w-full rounded-md border border-slate-300 bg-white px-3.5 py-3 text-lg font-italic-display italic text-cct-blue input-focus"
                style={{ fontFamily: "'Playfair Display', serif" }}
              />
              <span className="text-[11px] text-slate-500 mt-1 block">
                Your typed name will be stored as a legally-binding
                electronic signature, dated{" "}
                {new Date().toLocaleDateString()}.
              </span>
            </label>

            <label className="mt-5 flex items-start gap-2.5 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={agreedTerms}
                onChange={(e) => setAgreedTerms(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-[#1E3A8A]"
              />
              I have read and agree to the terms of the Cambridge College of
              Technology recruiter agreement.
            </label>

            <div className="mt-6 flex items-center justify-between gap-3">
              <div className="text-[12px] text-slate-500 inline-flex items-center gap-1">
                <ShieldCheck className="w-3.5 h-3.5 text-cct-blue" />
                Secured by SSL · eSignature timestamped
              </div>
              <button
                onClick={submit}
                disabled={submitting}
                className="inline-flex items-center gap-2 bg-cct-blue text-white font-semibold px-5 py-2.5 rounded-md hover:bg-[#1a3380] disabled:opacity-60"
              >
                {submitting ? "Signing..." : "Sign & Submit"}
                {!submitting && <CheckCircle2 className="w-4 h-4" />}
              </button>
            </div>
          </motion.div>
        )}
      </main>
      <Footer />
    </div>
  );
}
